'use client';
import React, { useEffect, useState } from 'react';
import { CommonProtos, QuoteProtos, ScannerProtos } from '@benzinga/scanner-manager';
import { Select, Table } from 'antd';
import { Pagination } from '@benzinga/core-ui';
import { ColumnsType, FilterValue, SorterResult, SortOrder, TablePaginationConfig } from 'antd/lib/table/interface';
import styled from '@benzinga/themetron';
import { PresetType } from './ShortInterestScanner';

export interface Option {
  name: string;
  value: string;
}

export interface FilterControl {
  fieldName: string;
  label: string;
  placeholder?: string;
  options: Option[];
}

export interface SimpleFilters {
  [key: string]: string;
}

export class Format {
  static percentage(value?: number) {
    if (value) {
      const number = Number(value.toFixed(2));
      return number ? `${Format.number(number)}%` : '-';
    } else {
      return '-';
    }
  }

  static colorfulChange(value?: number, render?: string | number) {
    let priceClass = 'text';

    if (value) {
      priceClass += value > 0 ? 'text text--success' : 'text text--danger';
    }

    const output = render !== undefined ? render : value;

    return <span className={priceClass}>{output}</span>;
  }

  static long(value?: number) {
    if (value) {
      return `${value.toFixed(2)}`;
    } else {
      return '-';
    }
  }

  static number(value?: number) {
    if (value) {
      return Number(value).toLocaleString('en');
    } else {
      return '-';
    }
  }

  static price(value?: number) {
    if (value) {
      return '$' + Format.number(Number(value.toFixed(2)));
    } else {
      return '-';
    }
  }
}

/**
 * Format a potentially very large number, human readable.
 */
export function formatLongHumanReadable(volume: number | undefined): string {
  if (!volume) {
    return '-';
  }

  if (volume < 1000) {
    return String(volume);
  }
  if (volume < 1e6) {
    return `${(volume / 1000).toFixed(2)}K`;
  }
  if (volume < 1e9) {
    return `${(volume / 1e6).toFixed(2)}M`;
  }
  if (volume < 1e12) {
    return `${(volume / 1e9).toFixed(2)}B`;
  }
  return `${(volume / 1e12).toFixed(2)}T`;
}

export function formatPrice(value: number) {
  const absValue = Math.abs(value);
  if (absValue < 1) {
    return value.toFixed(4);
  } else if (absValue < 10.0) {
    return value.toFixed(3);
  } else {
    return value.toFixed(2);
  }
}

/**
 * Used for ad-hod queries, or to replace "load()" at some point.
 */
async function scannerQuery(
  addr: string,
  key: string,
  query: ScannerProtos.Query,
  onResponse: (result: ScannerProtos.IQueryResponse) => void,
  //onResponse: (result: ScannerProtos.ScannerResult) => void,
  onError?: (error: CommonProtos.Err) => void,
) {
  const params = new URLSearchParams();

  if (key) {
    params.append('apikey', key);
  }

  params.append('fields', query.fields.join(','));

  if (query.filtersAsString) {
    query.filtersAsString.split(';').forEach(f => params.append('filter', f));
  }

  params.append('sortDir', ScannerProtos.SortDir[query.sortDir]);
  params.append('sortField', query.sortField ?? '');

  if (query.limit) {
    params.append('limit', String(query.limit));
  }

  if (query.offset) {
    params.append('offset', String(query.offset));
  }

  const uri = `${addr}?${params}`;
  const fetchInit: RequestInit = {
    // headers: { Accept: 'application/x-protobuf' },
    headers: { Accept: 'application/json' },
  };

  try {
    const response = await fetch(uri, fetchInit);

    //const data = await response.arrayBuffer();

    if (response.ok) {
      // const decodedData = ScannerProtos.ScannerResult.decode(new Uint8Array(data));
      // onResponse(decodedData);
      const data: ScannerProtos.IQueryResponse = await response.json();
      onResponse(data);
    } else if (response.status === 500) {
      if (onError) {
        onError(CommonProtos.Err.create({ message: response.statusText }));
      }
    } else {
      if (onError) {
        //const decodedError = CommonProtos.Err.decode(new Uint8Array(data));
        //onError(decodedError);
        onError(CommonProtos.Err.create({ message: response.statusText }));
      }
    }
  } catch (error) {
    if (onError) {
      onError(CommonProtos.Err.create({ message: `Network error: ${uri}: ${String(error)}` }));
    }
  }
}

export const SimpleScanner: React.FC<{
  scannerAddr: string;
  scannerKey?: string;
  columns: ColumnsType<QuoteProtos.IQuote>;
  query?: ScannerProtos.Query;
  filters: SimpleFilters;
  toolbar?: React.ReactElement[] | React.ReactNode[];
  tableSize?: { width?: number; height?: number };
  tableFooter?: React.ReactElement | React.ReactNode;
  extraFields?: string[];
  showPagination?: boolean;
  onFiltersChange: (filters: SimpleFilters) => void;
  filterControls: FilterControl[];
  onLoad?: (result: ScannerProtos.IQueryResponse) => void;
  initialData?: ScannerProtos.IQueryResponse | null;
}> = props => {
  const [filters, setFilters] = useState<SimpleFilters>(props.filters);
  const [results, setResults] = useState<QuoteProtos.IQuote[]>(props.initialData?.instruments || []);
  const [result, setResult] = useState<ScannerProtos.IQueryResponse | undefined>(props.initialData || undefined);
  const [query, setQuery] = useState<ScannerProtos.Query | undefined>(props.query);
  const [columns, setColumns] = useState(props.columns);
  const [loading, setLoading] = useState(props.initialData ? false : true);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    props.onFiltersChange(filters);
    if (!props.initialData) load();
  }, [filters, query]);
  /* eslint-enable react-hooks/exhaustive-deps */

  /**
   * A new query prop will reset the query fresh.
   */
  useEffect(() => {
    setQuery(props.query);
  }, [props.query]);

  const load = (requestQuery?: ScannerProtos.Query) => {
    const q = requestQuery ?? query;
    console.debug('SimpleScanner: load()', q);
    if (!q) {
      return;
    }

    setLoading(true);

    const sortedColumn = columns.find(column => column.sortOrder !== undefined);
    if (q.sortField) {
      columns.forEach(column => (column.sortOrder = undefined));
      const column = columns.find(column => column.key === q.sortField);
      if (column) {
        columns.forEach(column => (column.sortOrder = undefined));
        column.sortOrder = q.sortDir === ScannerProtos.SortDir.ASC ? 'ascend' : 'descend';
        setColumns([...columns]);
      } else {
        console.warn(`Column not found, query.sortField=${q.sortField}`);
      }
    } else {
      if (sortedColumn) {
        // Ensure no sort order is set.
        console.debug(`SimpleScanner: load() clearning sort columns`);
        columns.forEach(column => {
          column.sortOrder = undefined;
        });
        setColumns([...columns]);
      }
    }

    q.fields = props.columns.map((column: any) => column.dataIndex);
    if (props.extraFields) {
      q.fields = q.fields.concat(props.extraFields);
    }

    const handleResult = (result: ScannerProtos.IQueryResponse) => {
      console.debug('SimpleScanner: load() results', result);
      setResults(result.instruments || []);
      setResult(result);
      if (props.onLoad) {
        props.onLoad(result);
      }
      setLoading(false);
    };

    const handleError = () => {
      setLoading(false);
    };

    scannerQuery(props.scannerAddr, '', q, handleResult, handleError);
  };

  const controls = React.useMemo(() => {
    const updateFilters = (fieldName: string, value: string) => {
      const newFilters = { ...filters };
      if (value === '') {
        delete newFilters[fieldName];
      } else {
        newFilters[fieldName] = value;
      }
      setFilters(newFilters);
    };

    return (
      <>
        {props.filterControls.map(({ fieldName, label, options }) => {
          const filter = filters[fieldName];
          return (
            <SimpleScannerFilterItem key={fieldName}>
              <label>{label}</label>
              <div className="filter-content">
                <SelectFilter fieldName={fieldName} onChange={updateFilters} options={options} value={filter} />
              </div>
            </SimpleScannerFilterItem>
          );
        })}
      </>
    );
  }, [filters, props.filterControls]);

  const handleChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<QuoteProtos.IQuote> | SorterResult<any>[],
  ) => {
    if (!query) return;
    const newQuery = ScannerProtos.Query.fromObject(query);
    if (pagination.current && pagination.pageSize) {
      newQuery.offset = (pagination.current - 1) * pagination.pageSize;
    }
    if (sorter) {
      const sort = sorter as SorterResult<QuoteProtos.IQuote>;
      newQuery.sortDir = parseOrder(sort.order);
      newQuery.sortField = newQuery.sortDir === ScannerProtos.SortDir.NONE ? '' : (sort.field as string);
      setQuery(newQuery);
      load(newQuery);
    }
  };

  const pageSize = query?.limit ?? 100;
  const currentPage = Math.floor((query?.offset ?? 0) / pageSize) + 1;

  const handlePageChanged = (data: { nextPage: number; pageSize: number }) => {
    if (!query) return;
    const newQuery = ScannerProtos.Query.fromObject(query);
    newQuery.offset = (data.nextPage - 1) * data.pageSize;
    setQuery(newQuery);
    load(newQuery);
  };

  return (
    <SimpleScannerBlock minTableHeight={props?.tableSize?.height}>
      <SimpleScannerToolbar>
        {props.toolbar}
        {controls}
      </SimpleScannerToolbar>
      <SimpleScannerTableWrapper>
        <Table
          className="scanner-table"
          columns={columns}
          dataSource={results}
          footer={() => props.tableFooter}
          loading={loading}
          onChange={handleChange}
          pagination={false}
          rowKey="symbol"
          scroll={{ x: props?.tableSize?.width, y: props?.tableSize?.height }}
          showSorterTooltip={false}
        />
      </SimpleScannerTableWrapper>
      {props.showPagination && (result?.totalInstruments ?? 0) > pageSize && (
        <SimpleScannerPaginationWrap>
          <Pagination
            defaultPage={currentPage}
            onPageChanged={handlePageChanged}
            pageNeighbors={1}
            pageSize={pageSize}
            totalItems={result?.totalInstruments ?? 0}
          />
        </SimpleScannerPaginationWrap>
      )}
    </SimpleScannerBlock>
  );
};

function parseOrder(order?: SortOrder) {
  return order
    ? order === 'ascend'
      ? ScannerProtos.SortDir.ASC
      : ScannerProtos.SortDir.DESC
    : ScannerProtos.SortDir.NONE;
}

/**
 * A button, when clicked will apply the query to the scanner.
 */
export const Preset: React.FC<{
  active: PresetType;
  query: ScannerProtos.Query;
  label: string;
  name: PresetType;
  onClick: (query: ScannerProtos.Query, presetName: PresetType) => void;
}> = ({ active, label, name, onClick, query }) => {
  return (
    <SimpleScannerToolbarItem className={active === name ? 'active' : ''} onClick={() => onClick(query, name)}>
      {label}
    </SimpleScannerToolbarItem>
  );
};

const SelectFilter: React.FC<{
  fieldName: string;
  options: Option[];
  placeholder?: string;
  onChange: (fieldName: string, value: string) => void;
  value: string;
}> = ({ fieldName, onChange, options, placeholder, value }) => {
  return (
    <Select
      defaultValue=""
      dropdownClassName="simple-scanner-filter-select"
      onChange={(value: string) => onChange(fieldName, value)}
      placeholder={placeholder}
      size="small"
      style={{ width: '100%' }}
      value={value}
    >
      {options.map(o => (
        <Select.Option key={o.value} value={o.value}>
          {o.name}
        </Select.Option>
      ))}
    </Select>
  );
};

const SimpleScannerFilterItem = styled.div`
  margin: auto 5px 10px;
  width: 150px;

  label {
    font-size: smaller;
    display: block;
    margin-left: 2px;
  }
`;

const SimpleScannerToolbar = styled.div`
  display: flex;
  overflow-x: scroll;
`;

const SimpleScannerTableWrapper = styled.div`
  overflow-x: scroll;
`;

const SimpleScannerPaginationWrap = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
`;

const SimpleScannerToolbarItem = styled.div`
  border-bottom: solid 4px ${({ theme }) => theme.colorPalette.gray300};
  color: ${({ theme }) => theme.colors.foregroundMuted};
  cursor: pointer;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  padding: 6px 8px;
  margin: auto 5px 10px;
  &.active {
    color: ${({ theme }) => theme.colors.brand};
    border-bottom: solid 4px ${({ theme }) => theme.colors.brand};
  }
  @media (max-width: 800px) {
    margin-left: 0;
    padding: 0;
    margin-right: 8px;
  }
`;

const SimpleScannerBlock = styled.div<{ minTableHeight?: number | string }>`
  margin: 20px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  .text {
    &--danger {
      color: red;
    }

    &--success {
      color: green;
    }
  }

  .ant-table-footer {
    padding: 0;
    background-color: ${({ theme }) => theme.colors.backgroundActive} !important;
  }

  .ant-table-cell {
    padding: 5px !important;
  }

  .ant-table-body {
    min-height: ${props => (props.minTableHeight ? `${props.minTableHeight}px` : 'auto')} !important;
  }

  .ant-table-column-title {
    white-space: nowrap !important;
  }

  .ant-select-item-option-content {
    font-size: smaller !important;
  }

  .ant-select-selection-item {
    font-size: smaller !important;
  }

  .ant-table-thead > tr > th {
    background-color: ${({ theme }) => theme.colors.backgroundActive} !important;
  }

  .ant-table-content {
    border-top: solid 1px ${({ theme }) => theme.colors.border} !important;
  }

  .ant-table-container table > thead > tr:first-child {
    th:first-child,
    th:last-child {
      border-radius: 0 !important;
    }
  }
`;
